// src/components/common/RestaurantDropdown.js
import React, { useContext, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUtensils, FaChevronDown, FaCheck, FaSync } from 'react-icons/fa';
import LoadingAnimation from './LoadingAnimation';
import { RestaurantContext } from '../../context/RestaurantContext';

const DropdownContainer = styled.div`
  position: relative;
  margin-right: 1rem;
  
  @media (max-width: 768px) {
    margin-right: 0.5rem;
  }
`;

const DropdownButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  min-width: 200px;
  max-width: 250px;
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: none;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
      border-color: rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.95);
    }
  }
  
  /* Tablet */
  @media (max-width: 1024px) {
    min-width: 160px;
    max-width: 200px;
    padding: 0.45rem 0.65rem;
    font-size: 0.85rem;
    gap: 0.4rem;
  }
  
  /* Mobile */
  @media (max-width: 768px) {
    min-width: 130px;
    max-width: 160px;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    gap: 0.35rem;
  }
  
  /* Small mobile - compact version */
  @media (max-width: 500px) {
    min-width: 100px;
    max-width: 130px;
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const RestaurantIcon = styled(FaUtensils)`
  color: var(--primary-color);
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const RestaurantInfo = styled.div`
  flex: 1;
  text-align: left;
  overflow: hidden;
`;

const RestaurantName = styled.div`
  font-weight: 500;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 500px) {
    font-size: 0.75rem;
  }
`;

const RestaurantPlaceholder = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
`;

const ChevronIcon = styled(FaChevronDown)`
  font-size: 0.7rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
  color: inherit;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: rgba(15, 15, 15, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ItemIcon = styled.div`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: var(--primary-color);
    font-size: 0.8rem;
  }
`;

const ItemText = styled.div`
  flex: 1;
  font-size: 0.9rem;
  color: var(--text-primary-dark);
  font-weight: ${props => props.isSelected ? '600' : '400'};
`;

const CheckIcon = styled(FaCheck)`
  color: var(--success-color);
  font-size: 0.8rem;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--primary-color);
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isLoading ? 'rotate(360deg)' : 'rotate(0deg)'};
  }
`;

const DropdownTitle = styled.div`
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-primary-dark);
`;

const dropdownVariants = {
  hidden: { 
    opacity: 0, 
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.2
    }
  }
};

const RestaurantDropdown = ({ currentUser }) => {
  const { 
    restaurants, 
    selectedRestaurant, 
    selectRestaurant, 
    isLoading,
    error,
    refreshRestaurants
  } = useContext(RestaurantContext);
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRestaurantSelect = (restaurant) => {
    selectRestaurant(restaurant);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleRefresh = async () => {
    try {
      await refreshRestaurants();
    } catch (error) {
      console.error('Error refreshing restaurants:', error);
    }
  };

  // Don't render if no user is logged in
  if (!currentUser) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <DropdownContainer>
        <DropdownButton disabled>
          <RestaurantIcon />
          <RestaurantInfo>
            <LoadingAnimation size="small" inline />
            <RestaurantPlaceholder>Loading restaurants</RestaurantPlaceholder>
          </RestaurantInfo>
        </DropdownButton>
      </DropdownContainer>
    );
  }

  // Don't render if user has no restaurants
  if (restaurants.length === 0 && !isLoading) {
    return null;
  }

  // Show error state
  if (error) {
    return (
      <DropdownContainer>
        <DropdownButton onClick={handleRefresh}>
          <RestaurantIcon />
          <RestaurantInfo>
            <RestaurantPlaceholder>Error loading restaurants</RestaurantPlaceholder>
          </RestaurantInfo>
        </DropdownButton>
      </DropdownContainer>
    );
  }

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        onClick={toggleDropdown}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <RestaurantIcon />
        <RestaurantInfo>
          {selectedRestaurant ? (
            <RestaurantName>{selectedRestaurant.name}</RestaurantName>
          ) : (
            <RestaurantPlaceholder>
              {restaurants.length === 1 ? restaurants[0].name : 'Select Restaurant'}
            </RestaurantPlaceholder>
          )}
        </RestaurantInfo>
        <ChevronIcon isOpen={isOpen} />
      </DropdownButton>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenu
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={dropdownVariants}
          >
            <DropdownHeader>
              <DropdownTitle>
                Restaurants ({restaurants.length})
              </DropdownTitle>
              <RefreshButton 
                onClick={handleRefresh}
                disabled={isLoading}
                title="Refresh restaurants"
                isLoading={isLoading}
              >
                <FaSync />
              </RefreshButton>
            </DropdownHeader>
            
            {restaurants.map((restaurant, index) => (
              <DropdownItem
                key={restaurant._id}
                onClick={() => handleRestaurantSelect(restaurant)}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.05 }}
                whileHover={{ x: 5 }}
              >
                <ItemIcon>
                  {selectedRestaurant?._id === restaurant._id ? (
                    <CheckIcon />
                  ) : (
                    <FaUtensils />
                  )}
                </ItemIcon>
                <ItemText isSelected={selectedRestaurant?._id === restaurant._id}>
                  {restaurant.name}
                </ItemText>
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </AnimatePresence>
    </DropdownContainer>
  );
};

export default RestaurantDropdown;
